/**
 * An interaction is something that happened with the prospect at an address:
 * a knock, a call, an email. Fixing data afterwards is an edit (audited in
 * change_log), not a new interaction. See
 * docs/implementation/canvassing-crm-high-level-design.md §6.1.
 */

/**
 * Fixed system enum — the wizard branches on it and reports depend on it, so
 * it is not user-editable. Display names live in en.json under `outcomes.*`.
 */
export type OutcomeKind = 'no_answer' | 'objection' | 'lead' | 'do_not_contact' | 'other';

export const OUTCOME_KINDS: readonly OutcomeKind[] = ['no_answer', 'objection', 'lead', 'do_not_contact', 'other'];

/** Outcomes that mean a human was reached (used for contact-rate metrics). */
export const CONTACT_OUTCOME_KINDS: readonly OutcomeKind[] = ['objection', 'lead', 'do_not_contact'];

export interface Interaction {
  id: string;
  address_id: string;
  interaction_type: string; // interaction_types.id
  outcome_kind: OutcomeKind;
  objection_id: string; // objections.id when outcome_kind === 'objection'
  occurred_at: number;
  duration_seconds: number;
  notes: string;
  captured_lat: number | null;
  captured_lng: number | null;
  captured_accuracy: number | null;
  follow_up_at: number | null;
  lead_id: string;
  created_by: string;
  created_at: number;
  updated_by: string;
  updated_at: number;
  deleted_at: number | null;
}

export interface CreateInteractionParams {
  address_id: string;
  interaction_type: string;
  outcome_kind: OutcomeKind;
  objection_id?: string;
  occurred_at?: number;
  duration_seconds?: number;
  notes?: string;
  captured_lat?: number | null;
  captured_lng?: number | null;
  captured_accuracy?: number | null;
  follow_up_at?: number | null;
  /**
   * Required to log a contact against an address flagged do-not-contact.
   * Without it, `create` returns `{ success: false, reason: 'do_not_contact' }`.
   */
  override_do_not_contact?: boolean;
}

export interface UpdateInteractionParams {
  id: string;
  interaction_type?: string;
  outcome_kind?: OutcomeKind;
  objection_id?: string;
  occurred_at?: number;
  duration_seconds?: number;
  notes?: string;
  follow_up_at?: number | null;
  /** Links this interaction back to the lead it produced — see LeadService.create(). */
  lead_id?: string;
}

export type CreateInteractionResult =
  | { success: true; interaction: Interaction }
  | { success: false; reason: 'do_not_contact' | 'address_not_found' };

/** Column names that `update` may change, in one place for diffing and SQL. */
export const INTERACTION_EDITABLE_FIELDS = [
  'interaction_type',
  'outcome_kind',
  'objection_id',
  'occurred_at',
  'duration_seconds',
  'notes',
  'follow_up_at',
  'lead_id',
] as const;
